import { TutorialDefinition, TutorialScene } from '../TutorialEngine.js';
import { COLORS } from '../../rendering/Renderer.js';
import { JumpsVortexTutorialState } from './JumpsTutorialAdapter.js';

type VState = JumpsVortexTutorialState;

// Grid: 6/0-3:0F3,1C5,2D1,3A2  (Jérémy's vortex reference grid)
//   digit 0 @ F3 (SE)   digit 1 @ C5 (SW)   digit 2 @ D1 (NE)   digit 3 @ A2 (NW)
// Gate pair C3 ↔ E4 (SW ↔ SE), matching createJumpsVortexTutorialAdapter.
// Full solution: F3F4F5WC5C4C3E4D4ND1D2WA2A1A0ED0E0F0S|C3E4  (score 506: A=5, B=6)
//
// The 1 → 2 link is SW → NE (diagonal), impossible for a plain jump. Ramp `C5C4C3E4D4N`
// solves it by threading the gate: landing C5 (SW) → body into gate C3 → emerges at E4
// (SE) → launcher D4, which then jumps N into NE. A gate is the only way one ramp reaches
// the diagonally opposite quarter.
//
// The `|C3E4` suffix marks the pair active.

const SOLVED = 'F3F4F5WC5C4C3E4D4ND1D2WA2A1A0ED0E0F0S|C3E4';
// The gated ramp alone (landing C5 → gate C3 → E4 → launcher D4), for the teleport scene.
const GATED = 'C5C4C3E4D4N|C3E4';

const scenes: TutorialScene[] = [
    // Scene 1: Creation — activate a gate pair.
    {
        gameState: { ramps: '' } as VState,
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'Click Activate Vortex\nto place a gate pair', cell: 'B0', anchor: 'center' },
            { type: 'text-label', text: 'The pair must be linked\nin the Vortex Hub', cell: 'D2', anchor: 'center' },
        ],
        animate: {
            interval: 1500,
            loop: true,
            frames: [
                { ramps: '' },
                { ramps: '', gateMode: true },
                { ramps: '|C3E4', gateMode: true },
                { ramps: '|C3E4' },
            ] as VState[],
        },
    },

    // Scene 2: Activation — a ramp sitting on a gate cell blocks the pair.
    {
        gameState: { ramps: 'B3C3E', gateMode: true } as VState,  // ramp covers gate cell C3
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'Click a pair\nto activate it', cell: 'D0', anchor: 'center', color: COLORS.teal },
            { type: 'text-label', text: 'A ramp on a gate cell\nblocks activation', cell: 'B4', anchor: 'center', color: COLORS.amber },
        ],
    },

    // Scene 3: Teleport — a ramp built through the gate spans two quarters.
    {
        gameState: { ramps: GATED } as VState,
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'A ramp can pass\nthrough a gate...', cell: 'C2', anchor: 'center' },
            { type: 'text-label', text: '...and continue\nfrom the far side', cell: 'E5', anchor: 'center' },
            { type: 'pulse', cell: 'C3', color: COLORS.purple },
            { type: 'pulse', cell: 'E4', color: COLORS.purple },
        ],
    },

    // Scene 4: Solved — the gate is what reaches the diagonally opposite quarter.
    {
        gameState: { ramps: SOLVED } as VState,
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'A gate lets one\nramp cross to\nthe opposite quarter', cell: 'B0', anchor: 'center', color: COLORS.teal },
            { type: 'pulse', cell: 'C3', color: COLORS.purple },
            { type: 'pulse', cell: 'E4', color: COLORS.purple },
        ],
    },
];

/**
 * Vortex Jumps tutorial: 4 scenes on a 6×6 vortex grid with digits 0-3.
 * Teaches ramp teleportation through Vortex gates.
 */
export const JUMPS_VORTEX_TUTORIAL: TutorialDefinition = {
    gameType: 'jumps-vortex',
    gridCode: '6/0-3:0F3,1C5,2D1,3A2',
    scenes,
    previewScene: 3,
};
